import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type GetProductResult } from '@whinerp/client';
import { Button, ErrorState, Field, PageHeading, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';
import { ProductUnitPanel } from './product-unit-panel.js';

/**
 * SC-60 Product editor.
 *
 * `product` had a table, a generated search index and the quote builder's
 * picker reading it, and nothing that could put a row in it - products arrived
 * from the seed, so a real workspace had an empty catalogue and no way to fill
 * it. 04_Screens named the variant grid and the pricelist editor and never
 * named the screen that makes a product in the first place.
 *
 * The code carries its own help, because it is the field people get wrong: it
 * is what a quote line refers to, it is unique in the workspace, and the
 * refusal for a clash names the product already using it.
 *
 * Archive rather than delete. The product is on quotes that have been sent and
 * orders that have been placed, and the button says what it does - "Stop
 * selling this" - with a line saying those stay intact.
 */

type Product = GetProductResult['product'];

const BLANK = {
  sku: '',
  name: '',
  description: '',
  family: '',
  listPrice: '0.00',
  currency: 'AED',
  unit: 'each',
  active: true,
};

function ProductEditorScreen(): JSX.Element {
  const t = useT();
  const params = useParams();
  /*
   * `/products/new/edit` makes one; anything else opens it. The same
   * convention the report, segment and agent builders use.
   */
  const creating = (params['id'] ?? '') === 'new';
  const productId = creating ? '' : (params['id'] ?? '');

  const product = useQuery<GetProductResult, ApiError>({
    queryKey: ['product', productId],
    queryFn: () => api.getProduct({ path: { productId } }),
    enabled: !creating,
  });

  if (creating) return <ProductForm productId={null} initial={BLANK} />;
  if (product.isError) {
    return (
      <ErrorState
        title="That product could not be loaded"
        body={product.error.message}
        onRetry={() => void product.refetch()}
        retryLabel={t('action.retry')}
      />
    );
  }
  if (product.isPending) return <ProductEditorLoading />;

  const loaded: Product = product.data.product;
  return (
    <div className="flex w-full flex-col gap-4">
    <ProductForm
      // Keyed, so opening a different product rebuilds the form rather than
      // keeping the last one's values: the state is seeded from `initial`.
      key={loaded.productId}
      productId={loaded.productId}
      initial={{
        sku: loaded.sku,
        name: loaded.name,
        description: loaded.description ?? '',
        family: loaded.family ?? '',
        listPrice: loaded.listPrice,
        currency: loaded.currency,
        unit: loaded.unit,
        active: loaded.active,
      }}
    />
      <ProductUnitPanel productId={loaded.productId} baseUnitId={loaded.baseUnitId} />
    </div>
  );

}

/**
 * The form, at module scope.
 *
 * Declared inside the screen it would be a NEW component type on every render,
 * so React would unmount and remount it - and the values somebody was typing
 * would vanish the moment anything above re-rendered. It is a bug that looks
 * like a flaky form.
 */
function ProductForm({
  productId: id,
  initial,
}: {
  productId: string | null;
  initial: typeof BLANK;
}): JSX.Element {
  const t = useT();
  const errorMessage = useErrorMessage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [values, setValues] = useState(initial);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        sku: values.sku,
        name: values.name,
        description: values.description,
        family: values.family,
        listPrice: values.listPrice,
        currency: values.currency,
        unit: values.unit,
        active: values.active,
      };
      return id === null
        ? api.createProduct({ body })
        : api.updateProduct({ path: { productId: id }, body });
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['products', 'all'] });
      navigate(`/products/${result.product.productId}/edit`);
    },
  });

  const archive = useMutation({
    mutationFn: () => api.archiveProduct({ path: { productId: id ?? '' } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['product', id] });
      await queryClient.invalidateQueries({ queryKey: ['products', 'all'] });
      setValues((current) => ({ ...current, active: false }));
    },
  });

  /*
   * The value is read BEFORE the updater runs. React nulls `currentTarget`
   * when the handler returns and a state updater runs after that, so reading
   * it inside throws on the first keystroke.
   */
  const set = (key: keyof typeof BLANK) => (event: { currentTarget: { value: string } }) => {
    const next = event.currentTarget.value;
    setValues((current) => ({ ...current, [key]: next }));
  };

  return (
    <div data-component="ProductEditor" className="flex w-full max-w-reading flex-col gap-4">
      <PageHeading kicker={t('list.products.title')}>
        {id === null ? t('product.editor.new') : t('product.editor.edit')}
      </PageHeading>

      <Panel>
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate();
          }}
        >
          <Field
            label={t('product.editor.sku')}
            data-product-sku
            value={values.sku}
            onChange={set('sku')}
          />
          <span className="text-meta text-ink-meta">{t('product.editor.skuHelp')}</span>

          <Field
            label={t('product.editor.name')}
            data-product-name
            value={values.name}
            onChange={set('name')}
          />
          <Field
            label={t('product.editor.family')}
            data-product-family
            value={values.family}
            onChange={set('family')}
          />
          <Field
            label={t('product.editor.price')}
            data-product-price
            value={values.listPrice}
            onChange={set('listPrice')}
          />
          <span className="text-meta text-ink-meta">{t('product.editor.priceHelp')}</span>
          <Field
            label={t('product.editor.unit')}
            data-product-unit
            value={values.unit}
            onChange={set('unit')}
          />

          {save.isError ? (
            <p data-product-refusal className="text-dense text-danger-text">
              {errorMessage(save.error)}
            </p>
          ) : null}

          <div className="flex items-center gap-2">
            <Button type="submit" intent="primary" data-product-save busy={save.isPending}>
              {t('product.editor.save')}
            </Button>
            <Button intent="ghost" onClick={() => navigate('/products')}>
              {t('product.editor.cancel')}
            </Button>
          </div>
        </form>
      </Panel>

      {/*
        * Archiving, apart from the form and only once the product exists.
        *
        * Inside the form it reads as another field; it is a decision about
        * whether this is still sold. The line underneath says what it does NOT
        * do, because "stop selling" sounds like it might take the product off a
        * quote somebody has already sent.
        */}
      {id === null ? null : (
        <Panel>
          <div className="flex flex-col gap-2">
            <SectionLabel>{t('product.editor.archive')}</SectionLabel>
            <p className="text-meta text-ink-meta">{t('product.editor.archiveHelp')}</p>
            {values.active ? (
              <Button
                intent="secondary"
                data-product-archive
                busy={archive.isPending}
                onClick={() => archive.mutate()}
                className="self-start"
              >
                {t('product.editor.archive')}
              </Button>
            ) : (
              <div className="flex items-center gap-3">
                <span data-product-archived className="text-dense text-warning-text">
                  {t('product.editor.archived')}
                </span>
                <Button
                  intent="secondary"
                  data-product-restore
                  busy={save.isPending}
                  onClick={() => {
                    setValues((current) => ({ ...current, active: true }));
                    save.mutate();
                  }}
                >
                  {t('product.editor.restore')}
                </Button>
              </div>
            )}
          </div>
        </Panel>
      )}
    </div>
  );
}

function ProductEditorLoading(): JSX.Element {
  return (
    <div className="flex w-full max-w-reading flex-col gap-3" role="status" aria-busy="true">
      <span className="sr-only">Loading the product</span>
      {Array.from({ length: 5 }, (_, index) => (
        <Skeleton key={index} className="h-input w-full" />
      ))}
    </div>
  );
}

function ProductEditorError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="That product could not be loaded"
      body={error instanceof Error ? error.message : 'The product did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-60',
  route: '/products/:id/edit',
  surface: 'page',
  title: 'Product editor',
  features: ['F-046', 'F-161'],
  entitlement: 'F-046',
  reachedBy: [{ kind: 'screen', from: 'SC-55', how: 'the New button, or a row in the products list' }],
  components: [],
  elements: ['ProductEditor'],
  sampleRecord: { objectKey: 'product', param: 'id' },
  states: {
    empty: {
      notApplicable:
        'The screen IS the form: a product editor with nothing in it is a new product, which is the ' +
        'state it opens in at /products/new/edit.',
    },
    loading: ProductEditorLoading,
    error: ProductEditorError,
  },
  component: ProductEditorScreen,
});

export default screen;
